import { AppConfig } from "@fxid/shared";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

/**
 * @fileoverview Конфигурация и инициализация i18next и dayjs.
 * Этот файл содержит только чистую настройку библиотек, без логики приложения.
 */

// HACK: Локализации dayjs в формате UMD/CJS ожидают, что dayjs будет доступен
// в глобальной области видимости (window) или через require().
// При динамической загрузке локалей через import.meta.glob от Vite,
// их окружение не может разрешить 'dayjs', импортированный как ES-модуль.
// Явное присваивание `window.dayjs` решает эту проблему для динамически загружаемых скриптов локалей.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).dayjs = dayjs;

// Vite-специфичный способ для динамического импорта модулей из node_modules.
// import.meta.glob создает объект, где ключи - это пути к файлам,
// а значения - функции для асинхронного импорта этих файлов.
// Это позволяет Vite правильно бандлить локали dayjs для ленивой загрузки.
// Путь указывается от корня проекта.
const dayjsLocaleModules = import.meta.glob("/node_modules/dayjs/locale/*.js");
const buildVersion = import.meta.env.VITE_APP_BUILD_ID;

// Настройка детектора языка
const languageDetector = new LanguageDetector();
languageDetector.init({
	order: ["querystring", "cookie", "localStorage", "sessionStorage", "navigator", "htmlTag"]
});

/**
 * Получает язык, определенный детектором, и проверяет его наличие в списке поддерживаемых.
 * @returns {string} Код языка (например, 'en' или 'ru').
 */
export const getDetectedLanguage = (): string => {
	const detectedLangs = languageDetector.detect();
	const detectedLang = detectedLangs?.[0] ?? AppConfig.defaultLocale;
	// Убедимся, что язык поддерживается, иначе используем язык по умолчанию.
	return AppConfig.supportedLocales.includes(detectedLang) ? detectedLang : AppConfig.defaultLocale;
};

/**
 * Асинхронно меняет локаль для dayjs.
 * @param {string} lang - Новый код языка.
 */
export const changeDayjsLanguage = async (lang: string): Promise<void> => {
	// Английская локаль встроена по умолчанию и не требует импорта.
	if (lang === "en") {
		dayjs.locale(lang);
		return;
	}

	if (AppConfig.supportedLocales.includes(lang)) {
		// Формируем ключ для поиска в объекте, созданном import.meta.glob
		const localePath = `/node_modules/dayjs/locale/${lang}.js`;
		const importFn = dayjsLocaleModules[localePath];

		if (importFn) {
			try {
				// Вызываем функцию асинхронного импорта, которую предоставил Vite
				await importFn();
				dayjs.locale(lang);
				log.debug(`Day.js locale changed to: ${lang}`);
			} catch (error) {
				log.warn(`Could not load day.js locale for: ${lang}`, error);
				// В случае ошибки возвращаемся к локали по умолчанию
				dayjs.locale(AppConfig.defaultLocale);
			}
		} else {
			// Этот случай маловероятен, если SUPPORTED_LANGUAGE_CODES и локали dayjs синхронизированы
			log.warn(`Could not find day.js locale module for: ${lang}`);
			dayjs.locale(AppConfig.defaultLocale);
		}
	}
};

// Расширяем и настраиваем dayjs при инициализации
dayjs.extend(relativeTime);
dayjs.extend(duration);
void changeDayjsLanguage(getDetectedLanguage()); // Устанавливаем начальную локаль

// Формируем путь к файлам локализации.
const loadPath = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/locales/{{lng}}/translation.json?v=${buildVersion}`;

// Инициализация i18next
void i18next
	.use(languageDetector)
	.use(Backend)
	.use(initReactI18next)
	.init({
		fallbackLng: AppConfig.defaultLocale,
		// Указываем поддерживаемые языки. i18next будет приводить язык,
		// определенный детектором (например, en-US), к языку из этого списка (en).
		supportedLngs: AppConfig.supportedLocales,
		debug: process.env.NODE_ENV === "development",
		load: "languageOnly",
		interpolation: {
			escapeValue: false
		},
		react: {
			transKeepBasicHtmlNodesFor: ["br", "strong", "b", "i", "p"]
		},
		backend: {
			loadPath: loadPath
		}
	});

// Подписываемся на смену языка i18next, чтобы обновить локаль dayjs
i18next.on("languageChanged", (newLang) => {
	void changeDayjsLanguage(newLang);
});

export default i18next;
